(function () {
  if (window.BreatheScrollTracker) return;

  const STORAGE_KEY = 'breathe-journey-v1';
  const DEFAULT_DISTANCE_KM = 0;
  // One screen of scroll counts as ten metres, so a long visit adds up to
  // hundreds of metres rather than tens of kilometres.
  const KM_PER_SCREEN = 0.01;

  // The distance starts from zero every time the site is opened: a reload, or
  // arriving from outside the store (typed URL, bookmark, another site).
  // Moving between the store's own pages keeps the running total.
  function isNewVisit() {
    try {
      const entry = window.performance?.getEntriesByType?.('navigation')?.[0];
      if (entry && entry.type === 'reload') return true;
      if (entry && entry.type === 'back_forward') return false;
      const referrer = document.referrer ? new URL(document.referrer) : null;
      return !referrer || referrer.origin !== window.location.origin;
    } catch (_) {
      return true;
    }
  }

  function safeParse(value) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  }

  function loadState() {
    try {
      if (isNewVisit()) window.sessionStorage.removeItem(STORAGE_KEY);
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      const data = raw ? safeParse(raw) : null;

      if (!data || typeof data !== 'object') {
        return {
          distanceKm: DEFAULT_DISTANCE_KM,
          elapsedMs: 0,
          running: false,
          lastStartedAt: 0,
          timerTouched: false
        };
      }

      const distanceKm = Number(data.distanceKm);
      const elapsedMs = Number(data.elapsedMs);
      const lastStartedAt = Number(data.lastStartedAt);

      return {
        distanceKm: Number.isFinite(distanceKm) && distanceKm >= 0 ? distanceKm : 0,
        elapsedMs: Number.isFinite(elapsedMs) && elapsedMs >= 0 ? elapsedMs : 0,
        running: Boolean(data.running),
        lastStartedAt: Number.isFinite(lastStartedAt) && lastStartedAt > 0 ? lastStartedAt : 0,
        timerTouched: Boolean(data.timerTouched)
      };
    } catch (_) {
      return {
        distanceKm: DEFAULT_DISTANCE_KM,
        elapsedMs: 0,
        running: false,
        lastStartedAt: 0,
        timerTouched: false
      };
    }
  }

  const state = loadState();
  let prevY = window.scrollY;
  let persistTimeoutId = 0;

  function nowEpochMs() {
    return Date.now();
  }

  function viewportStepPx() {
    return Math.max(window.innerHeight || document.documentElement.clientHeight || 1, 1);
  }

  function getElapsedMs() {
    if (!state.running || !state.lastStartedAt) return state.elapsedMs;
    return state.elapsedMs + Math.max(0, nowEpochMs() - state.lastStartedAt);
  }

  function persist(force) {
    if (persistTimeoutId) {
      clearTimeout(persistTimeoutId);
      persistTimeoutId = 0;
    }

    const payload = {
      distanceKm: Number(state.distanceKm.toFixed(6)),
      elapsedMs: Math.max(0, Math.round(getElapsedMs())),
      running: state.running,
      lastStartedAt: state.running ? nowEpochMs() : 0,
      timerTouched: state.timerTouched
    };

    state.elapsedMs = payload.elapsedMs;
    state.lastStartedAt = payload.lastStartedAt;

    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      if (!force) return;
    }
  }

  function schedulePersist() {
    if (persistTimeoutId) return;
    persistTimeoutId = window.setTimeout(() => persist(false), 250);
  }

  function startTimer() {
    if (state.running) return;
    state.elapsedMs = getElapsedMs();
    state.running = true;
    state.lastStartedAt = nowEpochMs();
    state.timerTouched = true;
    persist(true);
  }

  function pauseTimer() {
    if (!state.running) return;
    state.elapsedMs = getElapsedMs();
    state.running = false;
    state.lastStartedAt = 0;
    state.timerTouched = true;
    persist(true);
  }

  function toggleTimer() {
    if (state.running) {
      pauseTimer();
    } else {
      startTimer();
    }
  }

  function initializeTimer({ autoStart = false } = {}) {
    if (autoStart && !state.timerTouched && !state.running && getElapsedMs() === 0) {
      startTimer();
    }
  }

  function getSnapshot() {
    return {
      distanceKm: state.distanceKm,
      elapsedMs: getElapsedMs(),
      running: state.running,
      started: state.distanceKm > 0 || getElapsedMs() > 0 || state.timerTouched,
      kmStepPx: viewportStepPx()
    };
  }

  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      const dy = Math.abs(y - prevY);

      if (dy > 0) {
        state.distanceKm += (dy / viewportStepPx()) * KM_PER_SCREEN;
        schedulePersist();
      }

      prevY = y;
    },
    { passive: true }
  );

  window.addEventListener('pagehide', () => persist(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persist(true);
  });

  window.setInterval(() => {
    if (state.running) persist(true);
  }, 1000);

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}

  window.BreatheScrollTracker = {
    get distanceKm() {
      return state.distanceKm;
    },
    get elapsedMs() {
      return getElapsedMs();
    },
    get running() {
      return state.running;
    },
    get started() {
      return state.distanceKm > 0 || getElapsedMs() > 0 || state.timerTouched;
    },
    initializeTimer,
    startTimer,
    pauseTimer,
    toggleTimer,
    getSnapshot
  };

  window.BreatheJourneyTracker = window.BreatheScrollTracker;
})();
